// 资料预览：从右边滑出的抽屉。地址里有 ?doc= 就打开（见 usePreview）。
// Markdown / 纯文本显示原文件；PDF、图片直接显示；其余（Word、网页……）显示解析出来的分块。

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, MessageSquarePlus, RotateCcw, X } from 'lucide-react'
import { fetchProtectedFile, getKnowledge, listChunks, previewKnowledge, reparseKnowledge } from '../../api/kb'
import type { Knowledge } from '../../api/types'
import { IconButton, Pill } from '../../ui'
import { MANIFEST_PATH, parseManifest, type LingzhiLinkManifest } from '../../lib/lingzhiLinks'
import { MarkdownDoc } from './MarkdownDoc'
import { docTitle, parseState, shortDate, sourceLabel } from './DocCard'
import { useDocPreview } from './usePreview'
import './sheet.css'

type Mode = 'markdown' | 'pdf' | 'image' | 'chunks'
const MD = new Set(['md', 'markdown', 'txt', 'text'])
const IMG = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'])
export function previewMode(doc: Knowledge): Mode {
  const t = (doc.file_type || '').toLowerCase()
  if (doc.type === 'manual' || MD.has(t)) return 'markdown'
  if (t === 'pdf') return 'pdf'
  if (IMG.has(t)) return 'image'
  return 'chunks'
}

function useManifest(doc: Knowledge | undefined) {
  const path = doc?.custom_metadata?.lingzhi_link_manifest
  return useQuery({
    queryKey: ['link-manifest', path],
    enabled: typeof path === 'string' && MANIFEST_PATH.test(path),
    staleTime: Infinity,
    queryFn: async (): Promise<LingzhiLinkManifest> => parseManifest(await (await fetchProtectedFile(path as string)).text()),
  })
}

/** 一篇资料的正文（抽屉和知识库阅读页共用）。 */
export function DocBody({ doc }: { doc: Knowledge }) {
  const mode = previewMode(doc)
  const manifest = useManifest(doc)
  const file = useQuery({
    queryKey: ['preview', doc.id, mode],
    enabled: mode !== 'chunks',
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) => previewKnowledge(doc.id, signal),
  })
  const chunks = useQuery({ queryKey: ['chunks', doc.id], enabled: mode === 'chunks', queryFn: () => listChunks(doc.id) })
  const [text, setText] = useState<string | null>(null)
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!file.data) return
    if (mode === 'markdown') {
      void file.data.text().then(setText)
      return
    }
    const u = URL.createObjectURL(file.data)
    setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [file.data, mode])

  if (parseState(doc.parse_status) === 'failed') return <ParseFailed id={doc.id} message={doc.error_message} />
  if (file.isError || chunks.isError) return <p className="sheet-note">没能载入原文：{(file.error ?? chunks.error)?.message}</p>
  if (mode === 'markdown') return text === null ? <div className="sheet-wait" aria-busy="true" /> : <MarkdownDoc text={text} manifest={manifest.data} />
  if (mode === 'pdf') return url ? <iframe className="sheet-pdf" src={url} title={doc.title} /> : <div className="sheet-wait" aria-busy="true" />
  if (mode === 'image') return url ? <img className="sheet-img" src={url} alt={doc.title} /> : <div className="sheet-wait" aria-busy="true" />
  if (chunks.isPending) return <div className="sheet-wait" aria-busy="true" />
  const parts = (chunks.data?.data ?? []).filter((c) => !c.chunk_type || c.chunk_type === 'text').sort((a, b) => a.chunk_index - b.chunk_index)
  if (!parts.length) return <p className="sheet-note">{parseState(doc.parse_status) === 'parsing' ? '还在解析，过一会儿再来看。' : '这篇没有可以显示的文字。'}</p>
  return (
    <>
      <MarkdownDoc text={parts.map((c) => c.content).join('\n\n')} manifest={manifest.data} />
      {(chunks.data?.total ?? 0) > parts.length && <p className="sheet-note">只显示了前 {parts.length} 段。</p>}
    </>
  )
}

/** 解析失败：说清原因，可以重新解析。 */
function ParseFailed({ id, message }: { id: string; message?: string }) {
  const qc = useQueryClient()
  const again = useMutation({ mutationFn: () => reparseKnowledge(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge-item', id] }) })
  return (
    <div className="sheet-note">
      <p>这篇解析失败了{message ? `：${message}` : ''}。</p>
      {again.isSuccess ? (
        <p role="status">已经重新提交解析，过一会儿再来看。</p>
      ) : (
        <Pill size="sm" icon={<RotateCcw />} onClick={() => again.mutate()} disabled={again.isPending}>
          重新解析
        </Pill>
      )}
      {again.isError && <p role="alert">没能重新解析：{again.error.message}</p>}
    </div>
  )
}

export function DocSheet() {
  const { docId, close } = useDocPreview()
  const navigate = useNavigate()
  const panel = useRef<HTMLElement>(null)
  const returnTo = useRef<HTMLElement | null>(null)
  const doc = useQuery({ queryKey: ['knowledge-item', docId], enabled: !!docId, queryFn: () => getKnowledge(docId!) })
  const open = !!docId

  // 打开时把焦点放进抽屉，关上时还给原来的位置；Esc 关闭
  useEffect(() => {
    if (!open) return
    returnTo.current = document.activeElement as HTMLElement | null
    panel.current?.focus()
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close()
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      returnTo.current?.focus?.()
    }
  }, [open, close])

  if (!open) return null
  const d = doc.data
  const source = d?.source && /^https?:\/\//.test(d.source) ? d.source : null
  return (
    <div className="sheet-root">
      <div className="sheet-scrim" onClick={close} aria-hidden="true" />
      <aside ref={panel} className="sheet" role="dialog" aria-modal="true" aria-label={d?.title ?? '资料预览'} tabIndex={-1}>
        <header className="sheet-head">
          {d && <span className="sk-chip">{sourceLabel(d)}</span>}
          {d?.folder_path && <span className="sk-chip">{d.folder_path}</span>}
          <span style={{ flex: 1 }} />
          <IconButton label="关闭" onClick={close}>
            <X />
          </IconButton>
        </header>
        <div className="sheet-body fade-edges">
          {doc.isError && <p className="sheet-note">没能打开这篇资料：{doc.error.message}</p>}
          {d && (
            <>
              <h2>{docTitle(d)}</h2>
              <div className="sheet-chips">
                <span className="sk-chip">{shortDate(d.created_at)} 收入</span>
                {d.tags?.map((t) => (
                  <span key={t.id} className="sk-chip">#{t.name}</span>
                ))}
              </div>
              {d.description && (
                <div className="sheet-summary">
                  <b>摘要</b>
                  {d.description}
                </div>
              )}
              <DocBody doc={d} />
            </>
          )}
        </div>
        {d && (
          <footer className="sheet-foot">
            <Pill variant="primary" icon={<MessageSquarePlus />} onClick={() => navigate('/', { state: { scope: { kind: 'doc', kbId: d.knowledge_base_id, knowledgeId: d.id, title: docTitle(d) } } })}>
              问这一篇
            </Pill>
            {source && (
              <a className="sk-pill" data-size="md" data-variant="soft" href={source} target="_blank" rel="noopener noreferrer">
                <ExternalLink width={16} height={16} />
                原网页
              </a>
            )}
          </footer>
        )}
      </aside>
    </div>
  )
}
